import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { Spinner } from "@/components/ui/spinner";
import { FieldGroup } from "@/components/ui/field";
import {
  BaseForm,
  FormChipList,
  FormCombobox,
  FormDatePicker,
  FormInput,
  FormMultiSelect,
  FormPhone,
  FormRecordPicker,
  FormTag,
  FormTextarea,
  FormToggle,
} from "components/BaseForm";
import TableIconBadge from "./TableIconBadge";
import { rowDisplayValue } from "./helpers";
import { resolveRelationshipTarget } from "./relationshipRoutes";
import ChoiceChip from "routes/custom_tables/grid/cells/ChoiceChip";
import { choiceChipStyle } from "routes/custom_tables/choiceHelpers";
import { upperFirst } from "helpers/string";
import ACL_RELATIONSHIPS from "acl-relationships";
import CustomTableRowService from "services/CustomTableRow";
import TagService from "services/Tag";
import useAuthorized from "hooks/useAuthorized";
import { useCustomTableSchema } from "hooks/useCustomTableSchema";
import { useCustomTableRowMutations } from "hooks/useCustomTableRowMutations";
import { useDynamicFormFields } from "hooks/useDynamicFormFields";
import { useDynamicZodSchema } from "hooks/useDynamicZodSchema";

// Tag metadata values flow back from `FormTag` keyed by `field_id`; the
// tag endpoints expect `id` (the metadata-field id) instead.
const metadataToPayload = tag =>
  (tag.tag_metadata_values ?? []).map(v => ({
    id: v.field_id,
    field_name: v.field_name,
    value: v.value,
  }));

const metadataSignature = tag =>
  JSON.stringify(
    (tag.tag_metadata_values ?? [])
      .map(v => [v.field_id, v.value ?? null])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
  );

// Diff the form's selected tags against the row's original set and call the
// tag endpoints. Re-adding an existing tag whose metadata changed upserts the
// new values idempotently (the model-tag row isn't duplicated server-side).
const syncRowTags = async (slug, rowId, originalTags, selectedTags) => {
  const modelType = `CustomTable:${slug}`;
  const originalById = new Map((originalTags ?? []).map(t => [t.id, t]));
  const selectedById = new Map((selectedTags ?? []).map(t => [t.id, t]));

  const additions = [...selectedById.values()].filter(tag => {
    const original = originalById.get(tag.id);
    return !original || metadataSignature(original) !== metadataSignature(tag);
  });
  const removals = [...originalById.values()].filter(
    tag => !selectedById.has(tag.id)
  );

  for (const tag of additions) {
    await TagService.addTag(tag, modelType, [rowId], metadataToPayload(tag));
  }
  for (const tag of removals) {
    await TagService.removeTag(tag, modelType, [rowId]);
  }
};

const initialValueForType = type => {
  if (type === "boolean") return false;
  if (type === "multi_select" || type === "list") return [];
  return null;
};

const buildInitialValues = (fields, row) =>
  Object.fromEntries(
    fields.map(f => {
      const v = row?.[f.name];
      if (v == null) return [f.name, initialValueForType(f.type)];
      return [f.name, v];
    })
  );

const choiceOptions = choices =>
  (choices ?? []).map(c => ({
    key: c.value,
    value: c.value,
    text: c.value,
    color: c.color,
  }));

const renderChoiceOption = option => (
  <ChoiceChip label={option.text} color={option.color} />
);

const renderControl = (field, common, row) => {
  // The descriptor's `multiline` hint distinguishes the two text types: long
  // text grows into a textarea, short_text stays a single-line input.
  if (field.multiline) {
    return <FormTextarea {...common} placeholder={field.label} />;
  }
  switch (field.type) {
    case "short_text":
      return <FormInput {...common} placeholder={field.label} />;
    case "number":
      return <FormInput {...common} type="number" />;
    case "email":
      return (
        <FormInput {...common} type="email" placeholder="name@example.com" />
      );
    case "url":
      return (
        <FormInput {...common} type="url" placeholder="https://example.com" />
      );
    case "phone":
      return <FormPhone {...common} placeholder="(555) 555-5555" />;
    // Text inputs (not type=number) so the decorated forms "85%" and
    // "$1,234.56" can be typed; the Zod schema strips the decoration. The
    // $/% adornments are display-only — the value stays the bare number.
    case "percentage":
      return <FormInput {...common} placeholder="85" suffix="%" />;
    case "currency":
      return <FormInput {...common} placeholder="0.00" prefix="$" />;
    case "date":
      return <FormDatePicker {...common} />;
    case "boolean":
      return <FormToggle {...common} />;
    case "single_select":
      return (
        <FormCombobox
          {...common}
          options={choiceOptions(field.choices)}
          renderOption={renderChoiceOption}
          renderValue={renderChoiceOption}
          clearable
        />
      );
    case "multi_select":
      return (
        <FormMultiSelect
          {...common}
          options={choiceOptions(field.choices)}
          renderOption={renderChoiceOption}
          getChipStyle={option => choiceChipStyle(option.color)}
        />
      );
    case "list":
      return <FormChipList {...common} placeholder="Add item…" />;
    case "relationship": {
      const target = resolveRelationshipTarget(
        field.column?.options?.target_type,
        field.column?.options
      );
      if (!target) return null;
      return (
        <FormRecordPicker
          {...common}
          fetchOptions={target.fetchOptions}
          optionsKey={field.column?.id}
          display={row?.[`${field.name}__display`]}
          placeholder={`Search ${field.label}…`}
        />
      );
    }
    default:
      return null;
  }
};

const DynamicField = ({ field, row }) => {
  const common = {
    name: field.name,
    label: field.label,
    required: field.required,
    id: `field-${field.name}`,
  };
  return renderControl(field, common, row) ?? null;
};

const CustomTableRowForm = ({ mode, slug: slugProp, rowId: rowIdProp }) => {
  const navigate = useNavigate();
  const params = useParams();
  const slug = slugProp ?? params.slug;
  const rowId = rowIdProp ?? params.rowId;
  const isEdit = mode === "edit";
  const canWrite = useAuthorized([ACL_RELATIONSHIPS.customTable.writeData]);
  const { schema, isFetched: isSchemaFetched } = useCustomTableSchema(slug);
  const fields = useDynamicFormFields(schema);
  const zodSchema = useDynamicZodSchema(schema);
  const { createRow, updateCell } = useCustomTableRowMutations(slug);
  const createdIdRef = useRef(null);
  // A table whose admin named a column ``tags`` owns that key, so the system
  // tag field steps aside (the dynamic field renders the column instead).
  const supportsTags = !(schema?.columns ?? []).some(
    c => c.field_id === "tags"
  );
  const formSchema = useMemo(
    () =>
      supportsTags ? zodSchema.extend({ tags: z.array(z.any()) }) : zodSchema,
    [zodSchema, supportsTags]
  );

  // Bounce non-writers to the grid rather than let them submit a form that
  // 403s on save. The schema DTO's per-table grant is authoritative; fall back
  // to the coarse writeData ACL only when it's absent.
  const perTableCan = schema?.current_user_can ?? null;
  const effectiveCanWrite = perTableCan ? perTableCan.write : canWrite;
  useEffect(() => {
    if (isSchemaFetched && !effectiveCanWrite) {
      navigate(`/custom_tables/${slug}`, { replace: true });
    }
  }, [isSchemaFetched, effectiveCanWrite, navigate, slug]);

  const { data: existingRow, isFetched: isRowFetched } = useQuery({
    queryKey: ["custom-table-row", slug, rowId],
    queryFn: () => CustomTableRowService.get(slug, rowId),
    enabled: isEdit && !!slug && !!rowId,
  });

  const initialValues = useMemo(() => {
    const row = isEdit ? existingRow : null;
    const values = buildInitialValues(fields, row);
    return supportsTags ? { ...values, tags: row?.tags ?? [] } : values;
  }, [fields, existingRow, isEdit, supportsTags]);

  const singular = schema?.singular_name?.trim() || schema?.display_name || "";
  const tableLabel = upperFirst(schema?.display_name ?? "");
  // Name the row being edited by its display-column value when set, so the
  // title reads "Edit Click: John Doe" rather than a bare "Edit Click".
  const displayValue = isEdit ? rowDisplayValue(schema, existingRow) : null;
  const pageTitle = isEdit
    ? `Edit ${singular}${displayValue ? `: ${displayValue}` : ""}`
    : `New ${singular}`;

  const handleCancel = () => {
    navigate(`/custom_tables/${slug}`);
  };

  const handleSubmit = async values => {
    // The system tag field is saved through the tag endpoints, not as a row
    // column; a table's own ``tags`` column stays in the row data.
    const { tags, ...rowValues } = values;
    const data = supportsTags ? rowValues : values;
    if (isEdit) {
      await updateCell({ rowId, data });
      if (supportsTags) {
        await syncRowTags(slug, rowId, existingRow?.tags ?? [], tags);
      }
    } else {
      const created = await createRow(data);
      createdIdRef.current = created?.id ?? null;
      if (supportsTags && created?.id != null) {
        await syncRowTags(slug, created.id, [], tags);
      }
    }
  };

  const handleSuccess = () => {
    if (isEdit) {
      navigate(`/custom_tables/${slug}/${rowId}`);
      return;
    }
    const id = createdIdRef.current;
    if (id != null) navigate(`/custom_tables/${slug}/${id}`);
  };

  if (!isSchemaFetched || (isEdit && !isRowFetched)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-muted-foreground">
        <Spinner />
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-muted-foreground">
        <p>Table not found.</p>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:bg-[var(--color-surface)] tw:border tw:border-[var(--color-border)] tw:rounded-lg tw:p-6 tw:text-[var(--color-text-primary)] tw:font-sans">
      <BaseForm
        enablePageTitle
        pageTitle={pageTitle}
        backLink={{
          to: `/custom_tables/${slug}`,
          label: `Back to ${tableLabel}`,
        }}
        pageIcon={<TableIconBadge iconName={schema.icon_name} />}
        schema={formSchema}
        defaultValues={initialValues}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        successHeader={isEdit ? "Row Updated" : "Row Created"}
        successMessage={`Successfully ${isEdit ? "updated" : "created"} the ${
          singular || "row"
        }.`}
      >
        <FieldGroup className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-6 tw:gap-y-5 tw:items-start">
          {fields.map(field => (
            <DynamicField
              key={field.name}
              field={field}
              row={isEdit ? existingRow : null}
            />
          ))}
          {supportsTags && (
            <FormTag name="tags" modelType={`CustomTable:${slug}`} />
          )}
        </FieldGroup>
      </BaseForm>
    </div>
  );
};

export default CustomTableRowForm;
